import { createBrowserHistory } from "history";
import { useEffect, useState } from "react";
import CookieBanner from "react-cookie-banner";
import { Route, Routes, useLocation, useNavigate } from "react-router";

import { UNAUTHORIZED_EVENT } from "api/apiInterceptor";

import { captureEvent } from "components/posthog/customEventCapture";
import PageViewTracker from "components/posthog/PageViewTracker";
import { ToasterNotification } from "components/toastNotification/ToasterNotification";
import { useRootStore } from "store";
import ErrorNotifications from "views/errorNotifications/containers/ErrorNotifications";
import LoginLayout from "views/layout/containers/Login";
import {
  EditingState,
  GlobalContextProvider,
  NavigationOptions,
  RedirectingState
} from "./store/GlobalContext";

import {
  getTabToken,
  hasPerformedInitialRedirect,
  setIntendedPath
} from "common/MetaActions";
import "styles/main.scss";
import Layout from "views/layout/containers/Default";
import "./App.scss";

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const { setStaticInformation } = useRootStore((state) => state.global);

  const { token, isAuthenticated, loginUser } = useRootStore(
    (state) => state.user
  );
  const [editingState, setEditingState] = useState<EditingState>({
    isEditing: false,
    location: null
  });

  const [redirectingState, setRedirectingState] = useState<RedirectingState>({
    url: "",
    isRedirecting: false,
    clickEvent: undefined,
    shouldHardRedirect: false
  });

  const handleNavigation = ({
    url,
    eventTitle,
    e,
    clickEvent,
    shouldHardRedirect = false
  }: NavigationOptions) => {
    if (eventTitle) {
      captureEvent(eventTitle);
    }
    if (editingState.isEditing) {
      e?.preventDefault();
      setRedirectingState({
        url,
        isRedirecting: true,
        clickEvent,
        shouldHardRedirect
      });
    } else {
      if (clickEvent) {
        clickEvent();
      }

      setEditingState({ isEditing: false, location: null });
      setRedirectingState({
        url: "",
        isRedirecting: false,
        clickEvent: undefined,
        shouldHardRedirect: false
      });

      if (shouldHardRedirect && typeof url === "string") {
        window.location.href = url;
      } else {
        if (typeof url === "number") {
          navigate(url); // navigate(-1)
        } else {
          navigate(url); // navigate("/path")
        }
      }
    }
  };

  if (getTabToken() && !token) {
    loginUser(getTabToken());
  }

  useEffect(() => {
    // Only save path if user is NOT authenticated
    if (!isAuthenticated) {
      const currentPath = location.pathname + location.search;

      // Define login-related routes that should NOT be saved
      const loginRoutes = [
        "/login",
        "/resetpassword",
        "/authenticate-internal-user",
        "/activateuser",
        "/setpassword",
        "/onetime-login",
        "/generate-onetime-login",
        "/inviteuser",
        "/teams-login-popup"
      ];

      // Check if current path is a login route
      const isLoginRoute = loginRoutes.some((route) =>
        currentPath.startsWith(route)
      );

      // Only save if NOT a login route and path is not root
      if (
        !isLoginRoute &&
        currentPath !== "/" &&
        !hasPerformedInitialRedirect()
      ) {
        setIntendedPath(currentPath);
      }
    }
  }, [isAuthenticated, location.pathname, location.search]);

  useEffect(() => {
    setStaticInformation();
    const cookieConsent = document.cookie
      .split("; ")
      .find((row) => row.startsWith("user-has-accepted-cookies"));
    if (cookieConsent && cookieConsent.split("=")[1] === "true") {
      posthogInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const posthogInit = async () => {
    if (process.env.REACT_APP_STAGE !== "production") {
      return;
    }

    const posthogApiKey = process.env.REACT_APP_POSTHOG_API_KEY;
    const posthogApiHost = process.env.REACT_APP_POSTHOG_API_HOST;
    if (posthogApiKey && posthogApiHost) {
      const posthog = await import("posthog-js");
      posthog.default.init(posthogApiKey, {
        api_host: posthogApiHost,
        autocapture: false,
        rageclick: false,
        capture_pageview: "history_change",
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        disable_web_experiments: true,
        ip: false,
        capture_dead_clicks: false
      });
    }
  };

  // Listen for unauthorized API responses
  useEffect(() => {
    const handleUnauthorized = (event: Event) => {
      // Guard against redirect if already on unauthorized page
      if (window.location.pathname !== "/unauthorized") {
        const status = (event as CustomEvent<{ status: number }>).detail?.status;
        navigate("/unauthorized", { state: { status } });
      }
    };

    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);

    return () => {
      window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized);
    };
  }, [navigate]);

  const link = (
    <a href="https://www.ludvig.se/cookies/" target="_blank" rel="noreferrer">
      Läs mer
    </a>
  );
  const history = createBrowserHistory();

  return (
    <GlobalContextProvider
      editingState={editingState}
      redirectingState={redirectingState}
      setEditingState={setEditingState}
      setRedirectingState={setRedirectingState}
      handleNavigation={handleNavigation}
    >
      <PageViewTracker history={history} />

      <CookieBanner
        message="Vi tar hjälp av cookies för att tillhandahålla våra tjänster. Genom att använda våra tjänster godkänner du att vi använder cookies. "
        link={link}
        buttonMessage="x"
        className="cookieBanner"
        disableStyle={true}
        onAccept={posthogInit}
        cookie="user-has-accepted-cookies"
        dismissOnScroll={false}
      />
      <Routes>
        <Route
          path="/*"
          element={isAuthenticated ? <Layout /> : <LoginLayout />}
        />
      </Routes>
      <ErrorNotifications />
      <ToasterNotification />
    </GlobalContextProvider>
  );
}
